import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement, ChangeEvent } from 'react'
import type { ITemplateParams, ITemplateRowData, TEMPLATE_STATUS, TEMPLATE_TYPE } from '~/types/template'

import columns from './columns'

import { templatesKeys, useTemplateListQuery } from '../queries'

import { activateTemplate } from '~/api/dynamic-screen'
import { getFile } from '~/api/file'
import { TemplateStatusBadge, TemplateTypeBadge } from '~/components/Badges'
import Button from '~/components/Buttons/Button'
import ContentLayout from '~/components/ContentLayout'
import Filters, { FiltersContent } from '~/components/Filters'
import SearchInput from '~/components/Inputs/Basic/SearchInput'
import SelectInput from '~/components/Inputs/Basic/SelectInput'
import Layout from '~/components/Layouts/Layout'
import Section from '~/components/Section'
import CustomControl from '~/components/SelectControls/CustomControl'
import CustomImageOption from '~/components/SelectOptions/CustomImageOption'
import TableWrapper from '~/components/Tables/TableWrapper'
import { DEFAULT_ITEMS_PER_PAGE_VALUE } from '~/const'
import { platformOptions } from '~/const/options'
import { isUnityOptions, orientationOptions, statusOptions, typeOptions } from '~/const/template'
import { useUserDetailsQueries } from '~/queries/users'
import { formatBuilder, isUserId } from '~/utils/builder'
import { downloadFromUrl } from '~/utils/file'
import { removeEmptyAttribute } from '~/utils/object'
import { displayToast } from '~/utils/toast'

import './styles.scss'

const initialFilters = {
  isUnity: isUnityOptions[0],
  orientation: orientationOptions[0],
  platform: platformOptions[0],
  status: statusOptions[0],
  title: '',
  type: typeOptions[0],
}

const ENTRIES_NAME = 'templates'

const formatStatusOptionLabel = (data: unknown) => {
  const { label, value } = data as IOption
  return value ? <TemplateStatusBadge value={value as TEMPLATE_STATUS} /> : label
}

const formatTypeOptionLabel = (data: unknown) => {
  const { label, value } = data as IOption
  return value ? <TemplateTypeBadge value={value as TEMPLATE_TYPE} /> : label
}

const TableView = (): ReactElement => {
  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const gridRef = useRef<AgGridReact>(null)

  const tableRef = useRef<HTMLDivElement>(null)

  const [search, setSearch] = useState('')

  const [selectedOrientation, setSelectedOrientation] = useState(initialFilters.orientation)

  const [selectedPlatform, setSelectedPlatform] = useState(initialFilters.platform)

  const [selectedStatus, setSelectedStatus] = useState(initialFilters.status)

  const [selectedType, setSelectedType] = useState(initialFilters.type)

  const [selectedIsUnity, setSelectedIsUnity] = useState(initialFilters.isUnity)

  const { mutateAsync: activateTemplateMutation } = useMutation({ mutationFn: activateTemplate })

  const { data: templates, isLoading: isTemplatesLoading } = useTemplateListQuery({
    size: DEFAULT_ITEMS_PER_PAGE_VALUE,
    ...removeEmptyAttribute({
      'device-type': selectedPlatform.value,
      is_unity: selectedIsUnity.value,
      orientation: selectedOrientation.value,
      search,
    }),
  })

  const updatedByQueries =
    templates?.data?.filter(({ updated_by }) => updated_by).map(({ updated_by }) => updated_by) || []

  const userQueries = useUserDetailsQueries(updatedByQueries)

  const userLoading = userQueries.some((e) => e.isLoading)

  const users = userLoading
    ? []
    : userQueries
        .map((result) => {
          const { data } = result
          if (data) {
            return data
          } else {
            return { name: '', user_id: '' }
          }
        })
        .filter((data) => !!data)

  const rowData: ITemplateRowData[] =
    templates?.data?.map(({ device_type, is_unity, template_key, updated_at, updated_by, ...template }) => ({
      ...template,
      deviceType: device_type,
      isUnity: is_unity,
      templateKey: template_key,
      updated_at_ts: updated_at,
      updated_by: isUserId(updated_by)
        ? users.find(({ user_id }) => user_id === updated_by)
        : { name: formatBuilder(updated_by) },
    })) || []

  const filteredData = rowData.filter(({ status, type }) => {
    if (selectedStatus.value && status !== selectedStatus.value) {
      return false
    }

    if (selectedType.value && type !== selectedType.value) {
      return false
    }

    return true
  })

  const handleSearch = (evt: ChangeEvent<HTMLInputElement>) => setSearch(evt.target.value)

  const handleOrientationChanged = (value: IOption) => setSelectedOrientation(value)

  const handlePlatformChanged = (value: IOption) => setSelectedPlatform(value)

  const handleStatusChanged = (value: IOption) => setSelectedStatus(value)

  const handleTypeChanged = (value: IOption) => setSelectedType(value)

  const handleIsUnityChanged = (value: IOption) => setSelectedIsUnity(value)

  const handleResetAll = () => {
    setSelectedOrientation(initialFilters.orientation)
    setSelectedPlatform(initialFilters.platform)
    setSelectedStatus(initialFilters.status)
    setSelectedType(initialFilters.type)
    setSearch(initialFilters.title)
  }

  const handleCreatePageContainer = (template: ITemplateRowData) => {
    const state: ITemplateParams = {
      deviceType: template.deviceType,
      isUnity: template.isUnity,
      key: template.templateKey,
      version: template.version,
    }
    navigate('/dynamic-screens/page-containers/creation', { state })
  }

  const handleActivateTemplate = async ({ deviceType, isUnity, templateKey, version }: ITemplateRowData) => {
    try {
      await activateTemplateMutation({ deviceType, isUnity, key: templateKey, version })
      displayToast('Template successfully activated', 'success')
      queryClient.invalidateQueries({ queryKey: templatesKeys.all })
    } catch {
      displayToast('Template could not be activated', 'error')
    }
  }

  const handleDownloadWorkspace = async (fileId: string) => {
    const { download_url } = await getFile({ fileId })
    downloadFromUrl(download_url)
  }

  return (
    <div className="templates-table-view">
      <Filters className="templates-filters">
        <FiltersContent>
          <div className="filters-row">
            <div className="inputs-container">
              <SearchInput
                className="template-search"
                onChange={handleSearch}
                placeholder="Search by title or key"
                value={search}
              />
            </div>
            <div className="inputs-container">
              <SelectInput
                className="template-select-filter"
                components={{ Control: CustomControl, Option: CustomImageOption }}
                isSearchable
                label="Orientation"
                onChange={(data) => handleOrientationChanged(data as IOption)}
                options={orientationOptions}
                value={orientationOptions.find((option) => option.value === selectedOrientation.value)}
              />
              <SelectInput
                className="template-select-filter"
                components={{ Control: CustomControl, Option: CustomImageOption }}
                isSearchable
                label="Platform"
                onChange={(data) => handlePlatformChanged(data as IOption)}
                options={platformOptions}
                value={platformOptions.find((option) => option.value === selectedPlatform.value)}
              />
              <SelectInput
                className="template-select-filter"
                components={{ Control: CustomControl }}
                isSearchable
                label="Is unity"
                onChange={(data) => handleIsUnityChanged(data as IOption)}
                options={isUnityOptions}
                value={isUnityOptions.find((option) => option.value === selectedIsUnity.value)}
              />
              <SelectInput
                className="template-select-filter"
                formatOptionLabel={formatStatusOptionLabel}
                isSearchable
                label="Status"
                onChange={(data) => handleStatusChanged(data as IOption)}
                options={statusOptions}
                value={statusOptions.find((option) => option.value === selectedStatus.value)}
              />
              <SelectInput
                className="template-select-filter"
                formatOptionLabel={formatTypeOptionLabel}
                isSearchable
                label="Type"
                onChange={(data) => handleTypeChanged(data as IOption)}
                options={typeOptions}
                value={typeOptions.find((option) => option.value === selectedType.value)}
              />
              <Button className="reset-button" ghost onClick={handleResetAll}>
                Reset all
              </Button>
            </div>
          </div>
        </FiltersContent>
      </Filters>
      <Layout className="templates-view__content" withPadding>
        <ContentLayout loading={isTemplatesLoading || userLoading}>
          <Section className="section">
            <TableWrapper
              className="templates-table with-min-height"
              columnDefs={columns}
              context={{ handleActivateTemplate, handleCreatePageContainer, handleDownloadWorkspace }}
              entriesName={ENTRIES_NAME}
              ref={gridRef}
              rowData={filteredData}
              suppressRowHoverHighlight
              tableRef={tableRef}
            />
          </Section>
        </ContentLayout>
      </Layout>
    </div>
  )
}

export default TableView
